import { alertsActions } from '.';
import { workdeskTicketsConstants } from 'store/constants';
import { WorkdeskTickets } from 'store/models/workdesk-tickets/WorkdeskTickets';
import { workdeskTicketsService } from 'store/services/workdesk-tickets.service';
import {
  justChangedAndFromWsTicketIdsSelector,
  newJustChangedTicketIdsSelector,
  workdeskTicketsSelector,
} from 'store/selectors/workdesk-tickets.selector';

import { ticketDataSelector } from 'store/selectors';
import { webSocketActions } from './websocket.actions';
import { ticketGroupTableSelectedIdsListSelector } from 'store/selectors/ticket-group-table.selector';
import { ticketGroupTableActions } from './ticket-group-table.actions';
import {
  loadParamsIgnoreHasLoadedSelector,
  relatedStatusTicketTypesObjSelector,
  selectedInboxesListSelector,
  workdeskFilterHasAnySearchValuesSelector,
  workdeskFilterWithPrevSelector,
  statusAndRelatedLastestUpdateDateSelector,
} from 'store/selectors/workdesk-filter.selector';
import { WS_REQ_UPDATE_EVENT } from 'store/constants/workdesk-tickets.constants';
import moment from 'utils/app-moment';
import { ticketActions } from './ticket.action';
import { workdeskFilterActions } from './workdesk-filter.actions';
import getErrorMessage from 'utils/getErrorMessage';
import { Statuses } from 'store/models/Statuses';
import { pingWsAfterSelector } from 'store/selectors/workdesk-filter.selector';
import { statusCountForTicketTypeSelector } from 'store/selectors/workdesk-filter.selector';
import {
  AllInboxesStatusLatestUpdateDate,
  AllInboxesStatusLatestUpdateDateI,
  EachStatusDateTime,
} from 'store/models/InboxesStatusLatestUpdateDate';
import { allInboxesStatusLatestUpdateDateSelector } from 'store/selectors/workdesk-tickets.selector';
import {
  IsJustChangeType,
  AllWorkdeskTicketsI,
  AssigneeIdToInfo,
  TicketsMap,
  WorkdeskTicketsResponse,
} from 'store/models/workdesk-tickets/workdesk-tickets.interface';
import { EachWorkdeskTicketI } from 'store/models/workdesk-tickets/each-workdesk-ticket.interface';
import { LoadWorkdeskTicketsParams } from 'store/models/workdesk-tickets/workdesk-tickets.interface';

const MAX_TICKETS_TO_UPDATE = 500;

/**
 * when get updated tickets from the web socket
 * @param data
 */
const wsLoadWorkdeskTicketsSuccess = (data) => {
  const success = (ticketIds: string[]) => {
    return {
      type: workdeskTicketsConstants.WS_LOAD_TICKETS_SUCCESS,
      payload: {
        ticketIds,
      },
    };
  };
  return (dispatch, getState) => {
    const {
      ticketsMap,
      assigneeIdToInfo,
      hasLoaded,
    } = workdeskTicketsSelector(getState());

    const wdTicketsHelper = new WorkdeskTickets(
      ticketsMap,
      assigneeIdToInfo,
      hasLoaded,
    );

    const newAssigneeIdToInfo = wdTicketsHelper.mergeAssigneeIdToInfo(
      data.assigneeIdToInfo,
    );

    const now = moment().format('YYYY-MM-DD HH:mm:ss');

    if (data.tickets.length > MAX_TICKETS_TO_UPDATE) {
      console.error(
        `websocket: tickets length exceed ${MAX_TICKETS_TO_UPDATE} (received ${data.tickets.length})`,
      );
    }

    const relatedObj = relatedStatusTicketTypesObjSelector(
      getState(),
    );
    const inboxes = selectedInboxesListSelector(getState());

    if (!inboxes.length) {
      console.error(
        `websocket inboxes is 0. not sure why we are receiving tickets. (${data.tickets.length})`,
      );
    }
    // ditch the tickets that are not in the current status
    const tickets = data.tickets
      .filter((ticket) => ticket.status in relatedObj)
      .filter((ticket) => inboxes.includes(ticket.inboxAddress))
      .slice(0, MAX_TICKETS_TO_UPDATE);

    const ticketIds = tickets.map((ticket) => ticket.id);
    dispatch(success(ticketIds));

    if (tickets.length) {
      console.info(`websocket get tickets update. ${tickets.length}`);
    } else {
      console.debug(`websocket get tickets update. ${tickets}`);
    }

    if (data.tickets.length !== tickets.length) {
      console.warn(
        `websocket invalid filtered length ${data.tickets.length}. filtered: ${tickets.length}`,
        'ori:',
        data.tickets,
        'filtered:',
        tickets,
        'relatedObj:',
        relatedObj,
      );
    }
    console.debug(`relatedObj:`, relatedObj);
    if (tickets.length > 0 && inboxes.length > 0) {
      dispatch(updateTickets(tickets, newAssigneeIdToInfo, now));

      // array -> already have InboxesStatuses... model and test case for the array version
      const oldAllInboxesStatusLatestUpdateDate = statusAndRelatedLastestUpdateDateSelector(
        getState(),
      );

      // find the lastest update date of the data received
      // object
      const statusTicketTypeLatestUpdateDate = WorkdeskTickets.getLatestUpdatedAtOfTicketsListByStatusTicketType(
        tickets,
        oldAllInboxesStatusLatestUpdateDate,
        relatedObj,
      );
      dispatch(
        updateAllStatusInboxesLatestUpdateDate(
          inboxes,
          statusTicketTypeLatestUpdateDate,
        ),
      );
    }
    dispatch(
      webSocketActions.updateStatusesInboxesLatestReceivedTime(
        relatedObj,
        inboxes,
        now,
      ),
    );

    if (tickets.length > 0) {
      const relatedObjWithTickets = WorkdeskTickets.getRelatedObjWithTickets(
        tickets,
      );
      dispatch(
        webSocketActions.updateStatusesInboxesLatestReceivedTimeWithData(
          relatedObjWithTickets,
          inboxes,
          now,
        ),
      );
    }
  };
};

export const loadWorkdeskTicketsfailure = (error, status) => {
  return {
    type: workdeskTicketsConstants.LOAD_TICKETS_ERROR,
    payload: { error, status },
  };
};

const wsLoadWorkdeskTicketsError = (error) => {
  return (dispatch, getState) => {
    dispatch(loadWorkdeskTicketsfailure(error, 'unknown'));
    dispatch(alertsActions.error(error));
  };
};

/**
 * web socket request update for the Main Page
 * request for the ticket's status and related statuses with the now update time
 * load with now -- moment().utc().format('YYYY-MM-DD HH:mm:ss')
 */
const mainPageWsRequestUpdateTickets = () => {
  return (dispatch, getState) => {
    const ticketData = ticketDataSelector(getState());
    if (!ticketData) return;
    dispatch(
      wsRequestUpdateTickets({
        inboxes: [ticketData.inbox],
        statusLatestUpdateDate: Statuses.getWSRequestStatusTicketTypes(
          ticketData.status,
        ).map((item) => ({
          ...item,
          latestUpdateDate: moment()
            .utc()
            .format('YYYY-MM-DD HH:mm:ss'),
        })),
      }),
    );
  };
};

/**
 * wsLoadWorkdeskTickets update
 * @param status
 * @param latestUpdateDate
 */
const wsRequestUpdateTickets = (selectedWsParams: {
  inboxes: string[];
  statusLatestUpdateDate: EachStatusDateTime[];
}) => {
  return (dispatch) => {
    if (
      selectedWsParams &&
      selectedWsParams.statusLatestUpdateDate &&
      selectedWsParams.inboxes.length > 0
    ) {
      const paramsToSend = {
        ...selectedWsParams,
        // convert to UTC time
        statusLatestUpdateDate: selectedWsParams.statusLatestUpdateDate.map(
          (item) => {
            const latestUpdateDateM = !moment(
              item.latestUpdateDate,
            ).isValid()
              ? moment()
              : moment(item.latestUpdateDate);
            return {
              ...item,
              latestUpdateDate: latestUpdateDateM
                .utc()
                .format('YYYY-MM-DD HH:mm:ss'),
            };
          },
        ),
      };
      console.info(`websocket request update ticket`, paramsToSend);
      dispatch(
        webSocketActions.wsEmit(WS_REQ_UPDATE_EVENT, paramsToSend),
      );
    }
  };
};

const updateAllStatusInboxesLatestUpdateDate = (
  inboxes: string[],
  statusTicketTypeLatestUpdateDate: {
    [status: string]: { [tickeType: string]: string | null };
  },
) => {
  if (!inboxes || !inboxes.length) {
    console.error(
      `inboxes length should be > 0 to use this function`,
    );
    return [];
  }
  return (dispatch, getState): EachStatusDateTime[] => {
    let finalResult = [] as EachStatusDateTime[];
    const oldLatestUpdateDateArray = statusAndRelatedLastestUpdateDateSelector(
      getState(),
    );
    // filter in only the entries that the old is before new
    const updated = Object.entries(
      statusTicketTypeLatestUpdateDate,
    ).reduce((result, [status, statusV]) => {
      // update if only is newer time
      result[status] = Object.entries(statusV).reduce(
        (result2, [ticketType, time]) => {
          const oldTime = oldLatestUpdateDateArray.find(
            (item) =>
              item.status === status &&
              item.ticketType === ticketType,
          )?.latestUpdateDate;
          if (!oldTime) result2[ticketType] = time;
          else if (moment(oldTime).isBefore(moment(time))) {
            result2[ticketType] = time;
          } else {
            // keep old time if the
            result2[ticketType] = oldTime;
          }
          return result2;
        },
        {},
      );
      return result;
    }, {});

    let oldAllInboxesStatusLatestUpdateDate,
      allInboxesStatusLatestUpdateDate;
    if (Object.keys(updated).length) {
      oldAllInboxesStatusLatestUpdateDate = allInboxesStatusLatestUpdateDateSelector(
        getState(),
      );
      allInboxesStatusLatestUpdateDate = new AllInboxesStatusLatestUpdateDate(
        oldAllInboxesStatusLatestUpdateDate,
      ).update(
        inboxes as string[],
        updated as {
          [status: string]: { [tickeType: string]: string | null };
        },
        true, // use update only
        false,
      ) as AllInboxesStatusLatestUpdateDateI;
      dispatch({
        type:
          workdeskTicketsConstants.UPDATE_ALL_INBOXES_STATUS_LATEST_UPDATE_DATE,
        payload: {
          allInboxesStatusLatestUpdateDate,
        },
      });

      finalResult = new AllInboxesStatusLatestUpdateDate(
        allInboxesStatusLatestUpdateDate,
      ).getStatusLatestUpdateDate(inboxes);
    }
    console.debug(`statusTicketTypeLatestUpdateDate: `, {
      statusTicketTypeLatestUpdateDate,
      updated,
      oldAllInboxesStatusLatestUpdateDate,
      allInboxesStatusLatestUpdateDate,
      finalResult,
    });
    return finalResult;
  };
};

const markIsJustChanged = (
  ids: string[],
  value: IsJustChangeType,
) => {
  return {
    type: workdeskTicketsConstants.MARK_IS_JUST_CHANGED,
    payload: {
      ids,
      value,
    },
  };
};

const checkAndMarkLoadedFromJustChanged = (
  newTicketIds: string[],
) => {
  return (dispatch, getState) => {
    const isJustChanged = newJustChangedTicketIdsSelector(getState());
    const inJustChanged = newTicketIds.filter((id) =>
      isJustChanged.includes(id),
    );
    if (inJustChanged.length) {
      dispatch(markIsJustChanged(inJustChanged, 'LOADED'));
    }
  };
};

/**
 * if no status passed will ping
 * @param status
 */
const loadWorkdeskTickets = (
  pingWsAfter = false,
  paramsInput?: LoadWorkdeskTicketsParams,
  pingOnly?: boolean,
  fromScroll = false,
) => {
  const request = (status) => {
    return {
      type: workdeskTicketsConstants.LOAD_TICKETS_REQUEST,
      payload: { status },
    };
  };
  const loadWorkdeskTicketsSuccess = (
    status: string,
    formattedTicketsCount: number,
    latestWithSearchTicketsCount?: number,
  ) => {
    return {
      type: workdeskTicketsConstants.LOAD_TICKETS_SUCCESS,
      payload: {
        status,
        formattedTicketsCount, // dispatch just to see the number in the action
        latestWithSearchTicketsCount,
      },
    };
  };

  return async (dispatch, getState) => {
    const workdeskFilterWithPrev = workdeskFilterWithPrevSelector(
      getState(),
    );
    const {
      ticketsMap,
      assigneeIdToInfo,
      hasLoaded,
      statusChangedIds,
    } = workdeskTicketsSelector(getState());
    const justChangedAndFromWsTicketIds = justChangedAndFromWsTicketIdsSelector(
      getState(),
    );
    if (!hasLoaded)
      throw Error('hasLoaded is missing from the store?');

    const hasAnySearchValues = workdeskFilterHasAnySearchValuesSelector(
      getState(),
    );

    /**
     * if starts with search then clear, when clear will load (return params)
     */
    const params = pingOnly
      ? undefined
      : paramsInput ||
        WorkdeskTickets.workdeskFilterToLoadParams(
          workdeskFilterWithPrev,
          hasAnySearchValues ? undefined : hasLoaded, // by pass checking hasLoaded if search value is passed
          hasAnySearchValues ? undefined : statusChangedIds,
          hasAnySearchValues
            ? undefined
            : justChangedAndFromWsTicketIds,
        );
    console.debug(`*** hasAnySearchValues`, hasAnySearchValues);
    if (!params) {
      console.info('***** no params so not load ******');
      if (pingWsAfter) {
        // for the ping
        const wsParams = WorkdeskTickets.workdeskFilterToLoadParams(
          workdeskFilterWithPrev,
        );

        if (wsParams) {
          const latestUpdateDateByStatus = statusAndRelatedLastestUpdateDateSelector(
            getState(),
          );
          const selectedWsParams = {
            inboxes: wsParams?.inboxes as string[],
            statusLatestUpdateDate: latestUpdateDateByStatus,
          };
          dispatch(wsRequestUpdateTickets(selectedWsParams));
        }
      }
      return;
    }

    console.debug('rest api:  load params', params);
    dispatch(request(params.status));

    const ticketsRestResult = await workdeskTicketsService
      .loadWorkdeskTickets(params)
      .then(
        async (
          data: WorkdeskTicketsResponse,
        ): Promise<EachWorkdeskTicketI[]> => {
          // format key assigneeInfo to assigneeId to info // TODO: refactor this part
          const formatted = {
            ...data,
            assigneeIdToInfo: data.assigneeInfo,
          };
          /* TODO: fix this new way */
          const wdTicketsHelper = new WorkdeskTickets(
            ticketsMap,
            assigneeIdToInfo,
            hasLoaded,
          );

          const newAssigneeIdToInfo = wdTicketsHelper.mergeAssigneeIdToInfo(
            formatted.assigneeIdToInfo,
          );

          if (formatted.tickets.length > MAX_TICKETS_TO_UPDATE) {
            console.error(
              `rest api: tickets length exceed ${MAX_TICKETS_TO_UPDATE} (received ${formatted.tickets.length})`,
            );
          }
          const tickets = formatted.tickets.slice(
            0,
            MAX_TICKETS_TO_UPDATE,
          );
          // question: we've never dispatch success?
          console.debug(`*** call update tickets `, tickets, params);
          dispatch(
            loadWorkdeskTicketsSuccess(
              params.status,
              formatted.tickets.length,
              hasAnySearchValues ? data.tickets.length : undefined,
            ),
          );
          dispatch(
            updateTickets(
              tickets,
              newAssigneeIdToInfo,
              undefined,
              params,
              undefined,
              fromScroll,
            ),
          );

          const newTicketIds = tickets.map((ticket) => ticket.id);
          // dispatch to change just changed = NEW (very pink) to LOADED (less pink)
          dispatch(checkAndMarkLoadedFromJustChanged(newTicketIds));

          // dispatch to update fromWsTicketIds (affect load on scroll, not affect the yellow)
          dispatch(removeIdsFromWsTicketsIds(newTicketIds));

          if (hasAnySearchValues) {
            dispatch(workdeskFilterActions.setHadCalledApi(true));
          }

          const oldAllInboxesStatusLatestUpdateDate = statusAndRelatedLastestUpdateDateSelector(
            getState(),
          );

          // find the lastest update date of the data received for this status
          const relatedObj = relatedStatusTicketTypesObjSelector(
            getState(),
          );
          const statusTicketTypeLatestUpdateDate = WorkdeskTickets.getLatestUpdatedAtOfTicketsListByStatusTicketType(
            tickets,
            oldAllInboxesStatusLatestUpdateDate,
            relatedObj,
          );

          const allInboxesStatusLatestUpdateDate: EachStatusDateTime[] = dispatch(
            updateAllStatusInboxesLatestUpdateDate(
              params.inboxes,
              statusTicketTypeLatestUpdateDate,
            ),
          );

          /* call web socket */
          if (pingWsAfter) {
            dispatch(
              wsRequestUpdateTickets({
                inboxes: params.inboxes,
                statusLatestUpdateDate: allInboxesStatusLatestUpdateDate,
              }),
            );
          }

          return tickets;
        },
      )
      .catch((error) => {
        const errorMessage = getErrorMessage(error);
        dispatch(
          loadWorkdeskTicketsfailure(errorMessage, params.status),
        );
        dispatch(alertsActions.error(error.toString()));
      });
    return ticketsRestResult;
  };
};

/**
 * fetch with the current workdesk filter but reset startAt (not sure if should increase the rowsPerPage though)
 * refetch, refresh
 */
export const refetchWorkdeskTickets = () => {
  return (dispatch, getState) => {
    let params = loadParamsIgnoreHasLoadedSelector(
      getState(),
    ) as LoadWorkdeskTicketsParams;
    if (!params) {
      console.error(`something is wrong. should have params`);
      return;
    }
    const pingWsAfter = pingWsAfterSelector(getState());
    params = { ...params, startAt: 0 };

    dispatch(removeTicketsAtParams(params));
    // TODO: here
    dispatch(loadWorkdeskTickets(pingWsAfter, params));
  };
};

export const removeTicketsAtParams = (
  params: LoadWorkdeskTicketsParams,
) => {
  return {
    type:
      workdeskTicketsConstants.REMOVE_TICKETS_IN_HASLOADED_AT_PARAMS,
    payload: {
      params,
    },
  };
};

const clearJustChangedTicketId = () => {
  return {
    type: workdeskTicketsConstants.CLEAR_JUST_CHANGE_TICKET_ID,
  };
};

const addStatusChangedIds = (statusChangedIds: string[]) => {
  return {
    type: workdeskTicketsConstants.ADD_STATUS_CHANGED_IDS,
    payload: {
      statusChangedIds,
    },
  };
};

const removeStatusChangedIds = (statusChangedIds: string[]) => {
  return {
    type: workdeskTicketsConstants.REMOVE_STATUS_CHANGED_IDS,
    payload: {
      statusChangedIds,
    },
  };
};

/**
 * has dispatch workdeskTicketsConstants.LOAD_TICKETS_SUCCESS
 * if has new ticketIds will dispatch load body html
 * TODO: check if this works with tickets having multiple statuses
 * @param tickets
 * @param assigneeIdToInfo
 * @param fromWs
 * @param params
 */
const updateTickets = (
  tickets: AllWorkdeskTicketsI,
  assigneeIdToInfo?: AssigneeIdToInfo,
  fromWsAt?: string,
  params?: LoadWorkdeskTicketsParams, // only use in the load workdesk tickets success
  skipUpdateMain = false,
  fromScroll = false,
) => {
  const updateTicketInTicketMap = (
    ticketsMap,
    hasLoaded,
    assigneeIdToInfo,
  ) => {
    return {
      type: workdeskTicketsConstants.UPDATE_TICKETS,
      payload: {
        ticketsMap,
        hasLoaded,
        assigneeIdToInfo,
        fromScroll, // just to track for now
      },
    };
  };
  return (dispatch, getState) => {
    const {
      ticketsMap,
      assigneeIdToInfo: oldAssigneeIdToInfo,
      hasLoaded,
      statusChangedIds: oldStatusChangedIds,
    } = workdeskTicketsSelector(getState());

    // remove statusChangedIds if the update includes this one
    const ticketIds = tickets.map((ticket) => ticket.id);
    if (oldStatusChangedIds) {
      const statusChangedIdsInNew = oldStatusChangedIds.filter((id) =>
        ticketIds.includes(id),
      );
      console.debug(
        `oldStatusChangedIds: `,
        oldStatusChangedIds,
        'statusChangedIdsInNew to be removed',
        statusChangedIdsInNew,
      );
      if (statusChangedIdsInNew && statusChangedIdsInNew.length) {
        dispatch(removeStatusChangedIds(statusChangedIdsInNew));
      }
    }
    // QUESTION: if from main page's assing-unassign. mark si-nsi, should the params be different?
    const loadParams =
      params || loadParamsIgnoreHasLoadedSelector(getState());
    const wdTicketsM = new WorkdeskTickets(
      ticketsMap,
      assigneeIdToInfo || oldAssigneeIdToInfo,
      hasLoaded,
    );
    const statusCount = statusCountForTicketTypeSelector(getState());

    const {
      ticketsMap: newTicketsMapUp,
      hasLoaded: newHasLoadedUp,
      newTicketIds, // ids that were not in the ticketsMap before
      statusChangedIds,
    } = wdTicketsM.mergeUpdatedTicketsAndHasLoaded(
      tickets,
      loadParams,
      statusCount, // is this used?
      fromWsAt,
      fromScroll,
    );
    dispatch(
      updateTicketInTicketMap(
        newTicketsMapUp,
        newHasLoadedUp,
        assigneeIdToInfo,
      ),
    );
    if (newTicketIds && newTicketIds.length) {
      console.debug(
        `newTicketIds (${newTicketIds.length}) found after merge ticket`,
      );
      dispatch(loadAllBodyHtmls(newTicketIds));
    }
    // TODO: add remove from selected if not in the current status
    const selectedIdList = ticketGroupTableSelectedIdsListSelector(
      getState(),
    );
    console.debug(
      `in update tickets actions: statusChangedIds: `,
      statusChangedIds,
    );

    // update statusChangedIds
    if (statusChangedIds.length) {
      dispatch(addStatusChangedIds(statusChangedIds));
    }

    const selectedIdsInChanged = selectedIdList.filter((id) =>
      statusChangedIds.includes(id),
    );
    if (selectedIdsInChanged?.length) {
      dispatch(
        ticketGroupTableActions.bulkSetSelectedIds(
          selectedIdsInChanged,
          false,
        ),
      );
    }

    if (!skipUpdateMain) {
      // check if match the current open ticket id (cover the case assign/unassign)
      // update the relavant info for that ticket as well
      const data = ticketDataSelector(getState());
      if (data && data.id) {
        const ticket = tickets.find(
          (ticket) => ticket.id === data.id,
        );
        if (ticket) {
          dispatch(
            ticketActions.updateTicketDataFromWorkdeskTicketInfo(
              ticket,
            ),
          );
        }
      }
    }
  };
};

const loadAllBodyHtmls = (ids: string[]) => {
  const request = (ids: string[]) => {
    return {
      type: workdeskTicketsConstants.LOAD_BODY_HTMLS_REQUEST,
      payload: {
        ids,
      },
    };
  };
  // might change
  const success = (ticketsMap: TicketsMap) => {
    return {
      type: workdeskTicketsConstants.LOAD_BODY_HTMLS_REQUEST_SUCCESS,
      payload: {
        ticketsMap, // might change
      },
    };
  };
  const failure = (error: string) => {
    return {
      type: workdeskTicketsConstants.LOAD_BODY_HTMLS_REQUEST_ERROR,
      payload: {
        error,
      },
    };
  };
  return async (dispatch, getState) => {
    dispatch(request(ids));
    try {
      const resData = await workdeskTicketsService.loadAllBodyHtmls(
        ids,
      );
      const {
        ticketsMap,
        assigneeIdToInfo,
        hasLoaded,
      } = workdeskTicketsSelector(getState());
      const wdTicketsM = new WorkdeskTickets(
        ticketsMap,
        assigneeIdToInfo,
        hasLoaded,
      );

      const newTicketsMap = wdTicketsM.addBodyHtmlLinkToTicketsMap(
        resData,
      );
      // TODO: add success
      dispatch(success(newTicketsMap));
    } catch (error) {
      const errorMessage = getErrorMessage(error);

      dispatch(failure(errorMessage as string));
      dispatch(alertsActions.error(errorMessage));
    }
  };
};

const setPreviewingBodyHtmlId = (id: string) => {
  return {
    type: workdeskTicketsConstants.PREVIEW_BODY_HTML_ID,
    payload: {
      id,
    },
  };
};

const closePreviewingBodyHtmlId = () => {
  return {
    type: workdeskTicketsConstants.CLOSE_PREVIEW_BODY_HTML_ID,
  };
};

const mergeTicketsToTicketsMap = (
  tickets: {
    id: string;
    bookingNo: string;
    bookingNos: string[];
    siKind?: string;
    urgency?: string;
  }[], // note: if going to change interface, double check WorkdeskTickets.mergeTickets parameter
) => {
  return {
    type: workdeskTicketsConstants.MERGE_TICKETS_TO_TICKETS_MAP,
    payload: {
      tickets,
    },
  };
};

const removeIdsFromWsTicketsIds = (ids: string[]) => {
  return {
    type: workdeskTicketsConstants.REMOVE_IDS_FROM_WS_TICKET_IDS,
    payload: {
      ids,
    },
  };
};

const reset = () => {
  return {
    type: workdeskTicketsConstants.RESET,
  };
};

export const workdeskTicketsActions = {
  loadWorkdeskTickets,
  loadAllBodyHtmls,
  clearJustChangedTicketId,
  setPreviewingBodyHtmlId,
  closePreviewingBodyHtmlId,
  wsRequestUpdateTickets,
  wsLoadWorkdeskTicketsSuccess,
  wsLoadWorkdeskTicketsError,
  updateTickets,
  updateAllStatusInboxesLatestUpdateDate,
  refetchWorkdeskTickets,
  mergeTicketsToTicketsMap,
  mainPageWsRequestUpdateTickets,
  checkAndMarkLoadedFromJustChanged,
  removeIdsFromWsTicketsIds,
  reset,
};
